import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { DoorOpen, DollarSign, ShoppingBag, Users, TrendingUp, Clock, AlertTriangle, Lock, Unlock, Gift, XCircle, RefreshCw } from 'lucide-react';

const categoryStyles = {
  fundador: 'bg-purple-100 text-purple-800',
  normal: 'bg-stone-100 text-stone-800',
  frecuente: 'bg-orange-100 text-orange-800',
  eventos: 'bg-blue-100 text-blue-800',
};

export default function DashboardPage() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(() => {
    axios.get(`${API}/dashboard`).then(r => {
      setData(r.data);
      setLastUpdate(new Date());
    }).catch(() => {}).finally(() => setLoading(false));
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const i = setInterval(fetchData, 20000); return () => clearInterval(i); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-64 text-stone-500">Cargando...</div>;
  if (!data) return <div className="text-stone-500">Error al cargar datos</div>;

  const kpis = [
    { label: 'Visitas Activas', value: data.active_visits, icon: DoorOpen, color: 'text-green-700 bg-green-50' },
    { label: 'Ventas Hoy', value: `$${data.today_revenue.toLocaleString()}`, icon: DollarSign, color: 'text-orange-700 bg-orange-50' },
    { label: 'Visitas Hoy', value: data.today_visits, icon: Clock, color: 'text-blue-700 bg-blue-50' },
    { label: 'Ticket Promedio', value: `$${data.avg_ticket.toLocaleString()}`, icon: TrendingUp, color: 'text-amber-700 bg-amber-50' },
    { label: 'Stock Bajo', value: data.low_stock_products || 0, icon: AlertTriangle, color: data.low_stock_products > 0 ? 'text-red-700 bg-red-50' : 'text-stone-700 bg-stone-100' },
    { label: 'Cortesias Hoy', value: data.today_experiences || 0, icon: Gift, color: 'text-purple-700 bg-purple-50' },
  ];

  const occupancyPct = data.locker_occupancy_pct || 0;
  const occupancyColor = occupancyPct > 80 ? 'text-red-700' : occupancyPct > 50 ? 'text-amber-700' : 'text-green-700';
  const barColor = occupancyPct > 80 ? 'bg-red-500' : occupancyPct > 50 ? 'bg-amber-500' : 'bg-emerald-500';
  const perRow = data.lockers_per_row || 10;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Panel de Control</h1>
          <p className="text-stone-500 text-sm mt-1">
            Resumen en tiempo real
            {lastUpdate && <span className="ml-2 text-stone-400">| {lastUpdate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
          </p>
        </div>
        <Button data-testid="refresh-dashboard" onClick={fetchData} variant="outline" className="rounded-full border-stone-300 text-sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children" data-testid="kpi-grid">
        {kpis.map((kpi, i) => (
          <Card key={i} className="bg-white border border-stone-200 shadow-sm rounded-2xl hover:shadow-md transition-shadow">
            <CardContent className="p-5 flex items-center gap-4">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${kpi.color}`}>
                <kpi.icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{kpi.label}</p>
                <p className="text-2xl font-outfit font-semibold text-stone-900 mt-0.5">{kpi.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Locker Occupancy Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-outfit text-xl font-medium text-stone-900">Ocupacion de Lockers</h2>
          <button data-testid="go-locker-map" onClick={() => navigate('/lockers')} className="text-sm text-orange-700 hover:text-orange-800 font-medium transition-colors">
            Ver mapa completo →
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Occupancy gauge */}
          <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
            <CardContent className="p-6 text-center">
              <div className="relative w-28 h-28 mx-auto mb-3">
                <svg className="w-28 h-28 -rotate-90" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="#E7E5E4" strokeWidth="10" />
                  <circle cx="60" cy="60" r="52" fill="none" stroke={occupancyPct > 80 ? '#EF4444' : occupancyPct > 50 ? '#F59E0B' : '#10B981'} strokeWidth="10"
                    strokeDasharray={`${occupancyPct * 3.267} ${326.7 - occupancyPct * 3.267}`}
                    strokeLinecap="round" className="transition-all duration-1000" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-2xl font-outfit font-bold ${occupancyColor}`}>{occupancyPct}%</span>
                </div>
              </div>
              <p className="text-sm font-medium text-stone-900">{data.occupied_lockers} / {data.total_lockers}</p>
              <p className="text-xs text-stone-500 mt-0.5">lockers ocupados</p>
            </CardContent>
          </Card>

          {/* Mini locker grid */}
          <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Vista rapida</p>
                <div className="flex gap-3 text-xs">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400"></span> Libre</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-400"></span> Ocupado</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1" data-testid="mini-locker-grid">
                {(data.locker_grid || []).map(l => (
                  <div
                    key={l.n}
                    title={l.s === 'o' ? `#${l.n} - ${l.c}` : `#${l.n} - Libre`}
                    className={`w-6 h-6 sm:w-7 sm:h-7 rounded flex items-center justify-center text-[9px] font-medium transition-all cursor-default ${
                      l.s === 'o'
                        ? 'bg-orange-400 text-white'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}
                  >
                    {l.n}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="font-outfit text-xl font-medium text-stone-900 mb-4">Actividad Reciente</h2>
        <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="recent-visits-table">
              <thead>
                <tr className="bg-stone-50/50">
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Cliente</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Categoria</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Mesero</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Locker</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Estado</th>
                  <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_visits.map(v => (
                  <tr key={v.id} className="border-t border-stone-100 hover:bg-stone-50/50 transition-colors">
                    <td className="p-4 text-sm text-stone-700 font-medium">{v.client_name}</td>
                    <td className="p-4"><Badge className={`${categoryStyles[v.client_category] || categoryStyles.normal} rounded-full text-xs px-2.5 py-0.5 border-0`}>{v.client_category}</Badge></td>
                    <td className="p-4 text-sm text-stone-600">{v.waiter_name}</td>
                    <td className="p-4 text-sm text-stone-600">{v.locker_number ? `#${v.locker_number}` : '-'}</td>
                    <td className="p-4">
                      <Badge className={`rounded-full text-xs px-2.5 py-0.5 border-0 ${v.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-stone-100 text-stone-600'}`}>
                        {v.status === 'active' ? 'Activa' : 'Cerrada'}
                      </Badge>
                    </td>
                    <td className="p-4 text-sm text-stone-900 font-semibold text-right">${(v.total || 0).toFixed(2)}</td>
                  </tr>
                ))}
                {data.recent_visits.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-stone-400 text-sm">Sin actividad reciente</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
